import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Icon } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { OTTO_LAST_MESSAGE_PREVIEW_LENGTH } from "@/lib/constants";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message: string;
}

interface OttoConversationsManagerProps {
  onSelectConversation: (conversationId: string) => void;
  onStartNewConversation: () => void;
}

export const OttoConversationsManager = ({
  onSelectConversation,
  onStartNewConversation,
}: OttoConversationsManagerProps) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchConversations = async () => {
    try {
      // Fetch conversations with message data in a single query
      const { data: conversationsData, error: conversationsError } = await supabase
        .from("otto_conversations")
        .select("id, title, created_at, updated_at")
        .order("updated_at", { ascending: false });

      if (conversationsError) throw conversationsError;

      if (!conversationsData || conversationsData.length === 0) {
        setConversations([]);
        return;
      }

      // Fetch all messages for these conversations in a single query
      const conversationIds = conversationsData.map(c => c.id);
      const { data: messagesData, error: messagesError } = await supabase
        .from("otto_messages")
        .select("conversation_id, content, created_at")
        .in("conversation_id", conversationIds)
        .order("created_at", { ascending: false });

      if (messagesError) throw messagesError;

      // Process messages to get counts and last message per conversation
      const messagesByConversation = new Map<string, { count: number; lastMessage: string }>();

      for (const msg of messagesData || []) {
        const existing = messagesByConversation.get(msg.conversation_id);
        if (!existing) {
          messagesByConversation.set(msg.conversation_id, {
            count: 1,
            lastMessage: msg.content.substring(0, OTTO_LAST_MESSAGE_PREVIEW_LENGTH),
          });
        } else {
          existing.count++;
        }
      }

      // Combine data
      const conversationsWithDetails: Conversation[] = conversationsData.map(conv => {
        const messageInfo = messagesByConversation.get(conv.id);
        return {
          ...conv,
          message_count: messageInfo?.count || 0,
          last_message: messageInfo?.lastMessage || "",
        };
      });

      setConversations(conversationsWithDetails);
    } catch (error) {
      console.error("Error fetching conversations:", error);
      toast.error("Failed to load conversations");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("otto_conversations")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setConversations((prev) => prev.filter((c) => c.id !== id));
      toast.success("Conversation deleted");
    } catch (error) {
      console.error("Error deleting conversation:", error);
      toast.error("Failed to delete conversation");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="loader" size={32} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Otto Conversations</h2>
          <p className="text-muted-foreground">
            View and continue previous conversations with Otto
          </p>
        </div>
        <Button onClick={onStartNewConversation} className="gap-2">
          <Icon name="add" size={16} />
          New Conversation
        </Button>
      </div>

      {conversations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Icon name="chat" size={48} className="text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No conversations yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Start chatting with Otto using the chat bubble in the corner
            </p>
            <Button onClick={onStartNewConversation} variant="outline" className="gap-2">
              <Icon name="chat" size={16} />
              Start Conversation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <ScrollArea className="h-[calc(100vh-280px)]">
          <div className="grid gap-4">
            {conversations.map((conversation) => (
              <Card
                key={conversation.id}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => onSelectConversation(conversation.id)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Icon name="chat" size={20} className="text-primary" />
                      <CardTitle className="text-base">{conversation.title}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(conversation.updated_at), "MMM d, yyyy h:mm a")}
                      </span>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Icon name="delete" size={16} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Conversation</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this conversation? This action
                              cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(conversation.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {conversation.last_message || "No messages yet"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {conversation.message_count} message{conversation.message_count !== 1 ? "s" : ""}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};
